import {
  Activity,
  BarChart3,
  Boxes,
  Building2,
  CalendarDays,
  ClipboardPenLine,
  HeartHandshake,
  Home,
  LayoutDashboard,
  ListChecks,
  PackageSearch,
  PawPrint,
  Scissors,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Stethoscope,
  UserRoundCog,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import type { ProCapability, ProWorkspaceType } from "./api";

export interface WorkspaceNavigationItem {
  to: string;
  label: string;
  icon: LucideIcon;
  capability: ProCapability;
  mobile?: boolean;
  end?: boolean;
  implemented?: boolean;
}

export interface WorkspaceDefinition {
  type: ProWorkspaceType;
  label: string;
  shortLabel: string;
  eyebrow: string;
  description: string;
  homeRoute: string;
  accent: string;
  icon: LucideIcon;
  navigation: WorkspaceNavigationItem[];
}

const veterinaryNavigation: WorkspaceNavigationItem[] = [
  {
    to: "/",
    label: "Inicio",
    icon: LayoutDashboard,
    capability: "VIEW_DASHBOARD",
    mobile: true,
    end: true,
  },
  {
    to: "/jornada",
    label: "Jornada",
    icon: Activity,
    capability: "MANAGE_APPOINTMENTS",
    mobile: true,
  },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, capability: "MANAGE_APPOINTMENTS" },
  {
    to: "/disponibilidad",
    label: "Horarios y bloqueos",
    icon: CalendarDays,
    capability: "MANAGE_SERVICES",
  },
  {
    to: "/atenciones",
    label: "Atenciones",
    icon: ClipboardPenLine,
    capability: "CLINICAL_WRITE",
    mobile: true,
  },
  {
    to: "/pacientes",
    label: "Pacientes",
    icon: PawPrint,
    capability: "VIEW_CLIENTS",
    mobile: true,
  },
  { to: "/vinculos", label: "Vínculos", icon: UsersRound, capability: "MANAGE_CLIENTS" },
  { to: "/tareas", label: "Tareas", icon: ListChecks, capability: "VIEW_DASHBOARD", mobile: true },
  { to: "/equipo", label: "Equipo", icon: UserRoundCog, capability: "MANAGE_TEAM" },
  { to: "/sedes", label: "Sedes", icon: Building2, capability: "MANAGE_LOCATIONS" },
  {
    to: "/configuracion",
    label: "Configuración",
    icon: Settings,
    capability: "MANAGE_ORGANIZATION",
  },
];

function foundationNavigation(
  homeRoute: string,
  activityLabel: string,
  activityIcon: LucideIcon,
  activityCapability: ProCapability,
): WorkspaceNavigationItem[] {
  return [
    {
      to: homeRoute,
      label: "Inicio",
      icon: Home,
      capability: "VIEW_DASHBOARD",
      mobile: true,
      end: true,
    },
    {
      to: `${homeRoute}/agenda`,
      label: "Agenda",
      icon: CalendarDays,
      capability: "MANAGE_APPOINTMENTS",
      mobile: true,
    },
    {
      to: `${homeRoute}/clientes`,
      label: "Mascotas",
      icon: PawPrint,
      capability: "VIEW_CLIENTS",
      mobile: true,
    },
    {
      to: `${homeRoute}/servicios`,
      label: "Servicios",
      icon: Boxes,
      capability: "MANAGE_SERVICES",
      mobile: true,
    },
    {
      to: `${homeRoute}/actividad`,
      label: activityLabel,
      icon: activityIcon,
      capability: activityCapability,
      mobile: true,
    },
    { to: `${homeRoute}/equipo`, label: "Equipo", icon: UserRoundCog, capability: "MANAGE_TEAM" },
    {
      to: `${homeRoute}/disponibilidad`,
      label: "Horarios y bloqueos",
      icon: CalendarDays,
      capability: "MANAGE_SERVICES",
    },
    {
      to: `${homeRoute}/reportes`,
      label: "Reportes",
      icon: BarChart3,
      capability: "VIEW_ANALYTICS",
    },
    {
      to: `${homeRoute}/configuracion`,
      label: "Configuración",
      icon: Settings,
      capability: "MANAGE_ORGANIZATION",
    },
  ];
}

function commercialNavigation(homeRoute: string): WorkspaceNavigationItem[] {
  return [
    {
      to: homeRoute,
      label: "Inicio",
      icon: Home,
      capability: "VIEW_DASHBOARD",
      mobile: true,
      end: true,
    },
    {
      to: `${homeRoute}/productos`,
      label: "Productos",
      icon: PackageSearch,
      capability: "VIEW_DASHBOARD",
      mobile: true,
    },
    {
      to: `${homeRoute}/inventario`,
      label: "Inventario",
      icon: Boxes,
      capability: "MANAGE_OFFERS",
      mobile: true,
    },
    {
      to: `${homeRoute}/campanas`,
      label: "Campañas",
      icon: Sparkles,
      capability: "VIEW_DASHBOARD",
      mobile: true,
    },
    {
      to: `${homeRoute}/analytics`,
      label: "Analytics",
      icon: BarChart3,
      capability: "VIEW_ANALYTICS",
      mobile: true,
    },
    {
      to: `${homeRoute}/configuracion`,
      label: "Configuración",
      icon: Settings,
      capability: "MANAGE_ORGANIZATION",
    },
  ];
}

export const WORKSPACE_REGISTRY: Record<ProWorkspaceType, WorkspaceDefinition> = {
  VETERINARY: {
    type: "VETERINARY",
    label: "Tappet Vet",
    shortLabel: "Veterinaria",
    eyebrow: "Continuidad clínica",
    description: "Jornada, pacientes e historia clínica autorizada en una única superficie.",
    homeRoute: "/",
    accent: "#7356c8",
    icon: Stethoscope,
    navigation: veterinaryNavigation,
  },
  COMMERCE: {
    type: "COMMERCE",
    label: "Tappet Commerce",
    shortLabel: "Commerce",
    eyebrow: "Operación comercial",
    description: "Catálogo, ofertas, beneficios y rendimiento del negocio.",
    homeRoute: "/commerce",
    accent: "#d75b7f",
    icon: ShoppingBag,
    navigation: commercialNavigation("/commerce"),
  },
  GROOMING: {
    type: "GROOMING",
    label: "Tappet Grooming",
    shortLabel: "Grooming",
    eyebrow: "Cuidado con detalle",
    description: "Agenda visual, servicios y evolución de cada sesión de grooming.",
    homeRoute: "/grooming",
    accent: "#c35f91",
    icon: Scissors,
    navigation: foundationNavigation("/grooming", "Sesiones", Sparkles, "VIEW_ACTIVITIES"),
  },
  TRAINING: {
    type: "TRAINING",
    label: "Tappet Training",
    shortLabel: "Training",
    eyebrow: "Progreso observable",
    description: "Objetivos, sesiones y continuidad del entrenamiento sin invadir clínica.",
    homeRoute: "/training",
    accent: "#287f79",
    icon: ShieldCheck,
    navigation: [
      ...foundationNavigation("/training", "Sesiones", Activity, "VIEW_ACTIVITIES").slice(0, 3),
      {
        to: "/training/planes",
        label: "Planes",
        icon: ListChecks,
        capability: "VIEW_CLIENTS",
        mobile: true,
      },
      ...foundationNavigation("/training", "Sesiones", Activity, "VIEW_ACTIVITIES").slice(3),
    ],
  },
  CARE: {
    type: "CARE",
    label: "Tappet Care",
    shortLabel: "Care",
    eyebrow: "Operación en movimiento",
    description: "Servicios de cuidado, paseos e incidentes desde una experiencia móvil.",
    homeRoute: "/care",
    accent: "#d17b36",
    icon: HeartHandshake,
    navigation: foundationNavigation("/care", "Sesiones", Activity, "VIEW_ACTIVITIES"),
  },
  SHELTER: {
    type: "SHELTER",
    label: "Tappet Shelter",
    shortLabel: "Refugio",
    eyebrow: "Custodia y adopción",
    description: "Identidad, publicaciones y seguimiento responsable de adopciones.",
    homeRoute: "/shelter",
    accent: "#b65f4b",
    icon: Home,
    navigation: [
      {
        to: "/shelter",
        label: "Inicio",
        icon: Home,
        capability: "VIEW_DASHBOARD",
        mobile: true,
        end: true,
      },
      {
        to: "/shelter/animales",
        label: "Animales",
        icon: PawPrint,
        capability: "VIEW_CLIENTS",
        mobile: true,
      },
      {
        to: "/shelter/adopciones",
        label: "Adopciones",
        icon: HeartHandshake,
        capability: "VIEW_CLIENTS",
        mobile: true,
      },
      {
        to: "/shelter/candidatos",
        label: "Candidatos",
        icon: ClipboardPenLine,
        capability: "MANAGE_ADOPTIONS",
        mobile: true,
      },
      {
        to: "/shelter/equipo",
        label: "Equipo",
        icon: UserRoundCog,
        capability: "MANAGE_TEAM",
        implemented: false,
      },
      {
        to: "/shelter/reportes",
        label: "Reportes",
        icon: BarChart3,
        capability: "VIEW_ANALYTICS",
        implemented: false,
      },
      {
        to: "/shelter/configuracion",
        label: "Configuración",
        icon: Settings,
        capability: "MANAGE_ORGANIZATION",
      },
    ],
  },
  PARTNER: {
    type: "PARTNER",
    label: "Tappet Partners",
    shortLabel: "Partners",
    eyebrow: "Inteligencia agregada",
    description: "Productos, campañas y analytics privados sin exponer identidades individuales.",
    homeRoute: "/partners",
    accent: "#40548f",
    icon: Boxes,
    navigation: commercialNavigation("/partners"),
  },
};

export function workspaceDefinition(type: ProWorkspaceType): WorkspaceDefinition {
  return WORKSPACE_REGISTRY[type];
}
